import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../contexts/I18nProvider";
import { useTheme } from "../../contexts/ThemeContext";
import Cookie, { JWT_COOKIE_NAME } from "../../lib/cookie";
import { Navbar } from "../../components/navbar";
import styles from "./Profile.module.css";
import { fetchUserProfile } from "../../redux/slices/profileSlice";
import ProfileHero from "./_components/ProfileHero";
import ProfileSidebar from "./_components/ProfileSidebar";
import ProfileContent from "./_components/ProfileContent";
import MobileTabNav from "./_components/common/MobileTabNav";

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, ready } = useTranslation();
  const { isDark } = useTheme();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  
  // Debug i18n in ProfileNew - only in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && false) {
      // Logging disabled for performance
      console.log("=== PROFILE I18N DEBUG ===");
      console.log("i18n ready:", ready);
    }
  }, [ready]);

  // Redux state
  const { user, loading, error, activeSection } = useSelector(
    (state) => state.profile
  );

  // Fetch profile data on component mount
  useEffect(() => {
    // Check if user is authenticated (has auth token in cookie)
    const authToken = Cookie.get(JWT_COOKIE_NAME);
    
    // Only fetch profile if authenticated
    if (authToken) {
      dispatch(fetchUserProfile());
    } else {
      // For development/testing, show a message but allow viewing the page
      console.warn("User not authenticated - profile data will not load");
      // In production, you would redirect to login:
      // navigate('/login');
    }
  }, [dispatch, navigate]);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className={`${styles.profile} ${isDark ? styles.dark : ""}`}>
        <div className={styles.loading}>
          <div className={styles.skeleton} style={{ width: "200px", height: "20px" }} />
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={`${styles.profile} ${isDark ? styles.dark : ""}`}>
        <div className={styles.error}>
          <strong>{t("error")}:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.profile} ${isDark ? styles.dark : ""}`}>
      <Navbar />
      {/* Hero Section */}
      <ProfileHero user={user} loading={loading} />

      {/* Main Content Area */}
      <div className={styles.content}>
        {/* Sidebar Navigation - Desktop Only */}
        {!isMobile && (
          <div className={styles.sidebar}>
            <ProfileSidebar activeSection={activeSection} />
          </div>
        )}

        {/* Main Content */}
        <main className={styles.main}>
          <ProfileContent activeSection={activeSection} />
        </main>
      </div>

      {/* Mobile Tab Navigation */}
      {isMobile && <MobileTabNav activeSection={activeSection} />}
    </div>
  );
};

export default Profile;
